import React from 'react';

export interface LoadingIndicatorProps {
  /**
   * Size of the loading spinner, default is 'medium'
   */
  size?: 'small' | 'medium' | 'large';
  
  /**
   * Custom text to display below the spinner, if not provided, no text is shown
   */
  text?: string;
  
  /**
   * Whether to display items being loaded, for context
   */
  items?: string[];
  
  /**
   * Additional CSS classes to apply to the container
   */
  className?: string;
  
  /**
   * Whether to center the loading indicator in its container
   */
  centered?: boolean;
  
  /**
   * Optional padding
   */
  padding?: 'none' | 'small' | 'medium' | 'large';
}

/**
 * A reusable loading indicator component with customizable size, text, and styling.
 */
export const LoadingIndicator: React.FC<LoadingIndicatorProps> = ({
  size = 'medium',
  text,
  items,
  className = '',
  centered = true,
  padding = 'medium'
}) => {
  // Spinner size classes
  const spinnerSizeClasses = {
    small: 'h-6 w-6 border-2',
    medium: 'h-10 w-10 border-4',
    large: 'h-16 w-16 border-6',
  };
  
  // Text size classes
  const textSizeClasses = {
    small: 'text-xs',
    medium: 'text-sm',
    large: 'text-base font-medium',
  };
  
  // Padding classes
  const paddingClasses = {
    none: 'p-0',
    small: 'p-3',
    medium: 'p-6',
    large: 'p-8',
  };
  
  // Container classes based on centered prop
  const containerClasses = centered
    ? 'flex flex-col items-center justify-center'
    : 'flex flex-col';
  
  return (
    <div className={`${containerClasses} ${paddingClasses[padding]} ${className}`}>
      <div 
        className={`
          ${spinnerSizeClasses[size]} 
          animate-spin 
          rounded-full 
          border-primary-light/30 
          dark:border-primary-dark/30 
          border-t-primary-light 
          dark:border-t-primary-dark
        `}
        aria-label="Loading"
      />
      
      {text && (
        <p className={`
          mt-4 
          ${textSizeClasses[size]} 
          text-text-primary-light 
          dark:text-text-primary-dark
        `}>
          {text}
        </p>
      )}
      
      {items && items.length > 0 && (
        <div className="mt-2 text-xs text-text-secondary-light dark:text-text-secondary-dark">
          <ul className="flex flex-wrap gap-2 justify-center">
            {items.map((item, index) => (
              <li 
                key={index} 
                className="px-3 py-1 bg-primary-light/10 dark:bg-primary-dark/10 rounded-full"
              >
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default LoadingIndicator; 